/*
 * Copyright 2022-2023 NXGN Management, LLC. All Rights Reserved.
 */

import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { LOCAL_STORAGE, StorageService } from "ngx-webstorage-service";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { achHeader, BillingDetail, ledgerHeader, Report } from "../models";
import { REST_CONSTANTS } from "../utils/data.rest.constants";
import { FormGroup } from "@angular/forms";

@Injectable({
  providedIn: "root"
})
export class ReportsService {
  billingData!: BillingDetail;

  public mobileViewStatus = new BehaviorSubject<boolean>(false);
  getMobileViewStatus = this.mobileViewStatus.asObservable();

  constructor(private http: HttpClient, @Inject(LOCAL_STORAGE) private localStorage: StorageService) {}

  // --------------------------------PAY-UI--------------------------------------
  setBillingObject(data: BillingDetail) {
    this.billingData = data;
  }

  getBillingObject(): BillingDetail {
    return this.billingData;
  }

  setMobileViewStatus(data: boolean) {
    this.mobileViewStatus.next(data);
  }

  // ----------------------------------------------------------------------

  getAchs(achReport: Report): Observable<Report[]> {
    let params = new HttpParams();
    params = params.append("mmid", achReport.mmid);
    params = params.append("startDate", achReport.startDate);
    params = params.append("endDate", achReport.endDate);
    params = params.append("status", achReport.status);
    params = params.append("response", achReport.response);
    const url = `${REST_CONSTANTS.IBF_REPORTING_BASE_URL}/ach`;
    const headers = REST_CONSTANTS.JSON_HEADER;
    const options = { headers: headers, params: params };

    return this.http.get<Report[]>(url, options).pipe(catchError(ReportsService.handleError));
  }

  getAchLedgers(achReport: Report): Observable<Report[]> {
    let params = new HttpParams();
    params = params.append("achDate", achReport.achDate);
    params = params.append("mmid", achReport.mmid);
    params = params.append("achType", achReport.achType);
    params = params.append("fundingId", achReport.fundingId);
    const url = `${REST_CONSTANTS.IBF_REPORTING_BASE_URL}/ach/ledgerItems`;
    const headers = REST_CONSTANTS.JSON_HEADER;
    const options = { headers: headers, params: params };

    return this.http.get<Report[]>(url, options).pipe(catchError(ReportsService.handleError));
  }

  getLedgers(ledgerReport: Report): Observable<Report[]> {
    let params = new HttpParams();
    params = params.append("mmid", ledgerReport.mmid);
    params = params.append("startDate", ledgerReport.startDate);
    params = params.append("endDate", ledgerReport.endDate);
    params = params.append("achStatus", ledgerReport.achStatus);
    params = params.append("status", ledgerReport.status);
    const url = `${REST_CONSTANTS.IBF_REPORTING_BASE_URL}/ledger`;
    const headers = REST_CONSTANTS.JSON_HEADER;
    const options = { headers: headers, params: params };

    return this.http.get<Report[]>(url, options).pipe(catchError(ReportsService.handleError));
  }

  getLedgerAchs(ledgerAch: Report): Observable<Report[]> {
    let params = new HttpParams();
    params = params.append("fundingDate", ledgerAch.fundingDate);
    params = params.append("mmid", ledgerAch.mmid);
    params = params.append("ledgerType", ledgerAch.ledgerType);
    const url = `${REST_CONSTANTS.IBF_REPORTING_BASE_URL}/ledger/ach`;
    const headers = REST_CONSTANTS.JSON_HEADER;
    const options = { headers: headers, params: params };

    return this.http.get<Report[]>(url, options).pipe(catchError(ReportsService.handleError));
  }

  downloadCsv(fileName: string, headerValues: achHeader[] | ledgerHeader[], data: any[], headerKey: string[]): void {
    const replacer = (_: any, value: any) => (value === null ? "" : value);
    const csv = data.map((row) => headerKey.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(","));
    csv.unshift(headerKey.join(","));
    csv[0] = JSON.stringify(headerValues).replace("[", "").replace("]", "");
    const link = document.createElement("a");
    link.setAttribute(
      "href",
      URL.createObjectURL(
        new Blob([csv.join("\r\n")], {
          type: "text/csv;charset=utf8;"
        })
      )
    );
    link.setAttribute("download", fileName + ".csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  exportSettlementDetailReport(settlementReport: Report): Observable<Blob> {
    let params = new HttpParams();
    params = params.append("startDate", settlementReport.startDate);
    params = params.append("endDate", settlementReport.endDate);
    const apiHeaders = new HttpHeaders({ "Content-Type": "application/vnd.ms-excel" });
    const url = `${REST_CONSTANTS.IBF_REPORTING_BASE_URL}/settlementDetail?` + params;
    return this.http
      .get<Blob>(url, {
        headers: apiHeaders,
        responseType: "blob" as "json"
      })
      .pipe(catchError(ReportsService.handleError));
  }

  exportFeesDetailReport(feeReport: Report): Observable<Blob> {
    let params = new HttpParams();
    params = params.append("startDate", feeReport.startDate);
    params = params.append("endDate", feeReport.endDate);
    const apiHeaders = new HttpHeaders({ "Content-Type": "application/vnd.ms-excel" });
    const url = `${REST_CONSTANTS.IBF_REPORTING_BASE_URL}/feeDetail?` + params;
    return this.http
      .get<Blob>(url, {
        headers: apiHeaders,
        responseType: "blob" as "json"
      })
      .pipe(catchError(ReportsService.handleError));
  }

  setChargebackFee(fee: Report): Observable<Report> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_V5}/globalconfig`;
    const headers = REST_CONSTANTS.JSON_HEADER;
    const options = { headers: headers };

    return this.http.post<Report>(url, fee, options).pipe(catchError(ReportsService.handleError));
  }

  getChargebackFee(): Observable<Report> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_V5}/globalconfig`;
    const headers = REST_CONSTANTS.JSON_HEADER;
    const options = { headers: headers };

    return this.http.get<Report>(url, options).pipe(catchError(ReportsService.handleError));
  }

  getDateResponse(form: FormGroup): void {
    if (form.get("endDate")?.value) {
      form.get("endDate")?.setValue(form.get("startDate")?.value);
    } else if (form.get("startDate")?.value) {
      form.get("startDate")?.setValue(form.get("endDate")?.value);
    }
  }

  createCsv(details: Blob, fileName: string) {
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(details));
    link.setAttribute("download", fileName + ".csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private static handleError(error: HttpErrorResponse) {
    return throwError(error.error);
  }
}
